import { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { PostComposer, AdaptContext } from '../components/PostComposer';
import { PostPreview } from '../components/PostPreview';
import { useSocialAccounts } from '../hooks/useSocialAccounts';
import { usePostComposer } from '../hooks/usePostComposer';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Eye, X } from 'lucide-react';
import { toast } from '@/components/ui/use-toast';
import {
  AdaptedPlatformContent,
  MediaAttachment,
  PlatformPostSettings,
  SocialMediaPost,
  SocialPostStatus,
} from '@/lib/api';
import { socialMediaApi } from '../api';
import { TrashIcon } from '../components/Icons';
import { useHolderSwitcher } from '@/components/HolderSwitcher';

const PAGE_TITLES: Record<SocialPostStatus, string> = {
  draft: 'Edit Draft',
  scheduled: 'Scheduled Post',
  publishing: 'Publishing Post',
  published: 'Published Post',
  failed: 'Failed Post',
  cancelled: 'Cancelled Post',
};

export function ComposerPage() {
  const { postId } = useParams<{ postId?: string }>();
  const navigate = useNavigate();
  const { selectedHolderId } = useHolderSwitcher();
  const { accounts } = useSocialAccounts(selectedHolderId);
  const [existingPost, setExistingPost] = useState<SocialMediaPost | null>(null);
  const [isLoadingPost, setIsLoadingPost] = useState(!!postId);
  const [mediaAttachments, setMediaAttachments] = useState<MediaAttachment[]>([]);

  useEffect(() => {
    if (!postId || !selectedHolderId) return;
    let cancelled = false;
    socialMediaApi
      .getSocialMediaPost(selectedHolderId, postId)
      .then((post) => {
        if (!cancelled) {
          setExistingPost(post);
          setIsLoadingPost(false);
        }
      })
      .catch(() => {
        if (!cancelled) setIsLoadingPost(false);
      });
    return () => {
      cancelled = true;
    };
  }, [postId, selectedHolderId]);

  const {
    post,
    content,
    setContent,
    title,
    setTitle,
    notes,
    setNotes,
    targetAccountIds,
    setTargetAccountIds,
    tags,
    setTags,
    isSaving,
    error,
    saveDraft,
    adaptContent,
    schedulePost,
    publishNow,
    resetToDraft,
  } = usePostComposer(selectedHolderId, existingPost ?? undefined);

  const [isAdapting, setIsAdapting] = useState(false);
  const [editedPlatformText, setEditedPlatformText] = useState<Record<string, string>>({});
  const [link, setLink] = useState(existingPost?.link ?? '');
  const [perPlatformSettings, setPerPlatformSettings] = useState<
    Record<string, Partial<PlatformPostSettings>>
  >({});
  const [approvedPlatforms, setApprovedPlatforms] = useState<Record<string, boolean>>({});

  // Sync link when the existing post loads asynchronously
  useEffect(() => {
    if (existingPost) {
      setLink(existingPost.link ?? '');
    }
  }, [existingPost]);

  // When loading an existing post that has platform content, auto-approve all platforms
  // so the user doesn't need to re-approve content they already reviewed before saving.
  useEffect(() => {
    if (!existingPost?.platformContent) return;
    const restoredApprovals: Record<string, boolean> = {};
    Object.keys(existingPost.platformContent).forEach((platform) => {
      restoredApprovals[platform] = true;
    });
    setApprovedPlatforms(restoredApprovals);
  }, [existingPost?.postId]);

  const confirmedFileIds = mediaAttachments
    .filter((a) => a.uploadState === 'done')
    .map((a) => a.fileId);

  const handleAdapt = async (
    platforms?: string[],
    context?: AdaptContext,
    platformSettings?: Record<string, { format?: string }>,
  ) => {
    setIsAdapting(true);
    // Ensure the post exists before adapting. When there's no post yet, save first
    // and pass the returned postId directly — the adaptContent callback captures
    // post from its closure and would see the stale null value otherwise.
    let overridePostId: string | undefined;
    if (!post && content.trim()) {
      const saved = await saveDraft(undefined, confirmedFileIds, link || null);
      if (!saved) {
        setIsAdapting(false);
        return;
      }
      overridePostId = saved.postId;
    }
    await adaptContent(platforms, context, platformSettings, overridePostId);
    setIsAdapting(false);
  };

  const handleSchedule = async (publishAt: string) => {
    let currentPostId = post?.postId;
    if (!currentPostId) {
      const saved = await saveDraft(undefined, confirmedFileIds, link || null);
      if (!saved) return;
      currentPostId = saved.postId;
    }
    const scheduled = await schedulePost(publishAt, currentPostId);
    if (scheduled) {
      navigate('/a/arche_social_media');
    }
  };

  const handleSavePlatformEdits = async (
    platformContent: Record<string, AdaptedPlatformContent>,
  ) => {
    if (!post || !selectedHolderId) return;
    await socialMediaApi.updateSocialMediaPost(selectedHolderId, post.postId, { platformContent });
  };

  const handlePublishNow = async (approvedAccountIds?: string[]) => {
    const activeAccounts = accounts.filter((a) => a.status === 'active');
    const effectiveAccountIds =
      approvedAccountIds && approvedAccountIds.length > 0
        ? approvedAccountIds
        : targetAccountIds.length > 0
          ? targetAccountIds
          : activeAccounts.map((a) => a.accountId);

    if (effectiveAccountIds.length === 0) return;

    const saved = await saveDraft(effectiveAccountIds, confirmedFileIds, link || null);
    if (!saved) return;

    const results = await publishNow(saved.postId);
    if (results) {
      navigate(`/a/arche_social_media/compose/${saved.postId}`);
    }
  };

  /**
   * Handles "Unschedule" (scheduled → draft) and "Save as Draft" (failed → draft).
   * For failed posts, any unsaved content edits are saved first before resetting status.
   */
  const handleResetToDraft = async () => {
    if (post?.status === 'failed' && content.trim()) {
      await saveDraft(undefined, confirmedFileIds, link || null);
    }
    await resetToDraft();
  };

  const [isDeleting, setIsDeleting] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  // Swipe-down-to-close for bottom sheet
  const sheetRef = useRef<HTMLDivElement>(null);
  const dragStartY = useRef<number | null>(null);

  const handleSheetTouchStart = (e: React.TouchEvent) => {
    dragStartY.current = e.touches[0].clientY;
  };

  const handleSheetTouchEnd = (e: React.TouchEvent) => {
    if (dragStartY.current === null) return;
    const delta = e.changedTouches[0].clientY - dragStartY.current;
    if (delta > 60) setIsPreviewOpen(false);
    dragStartY.current = null;
  };

  const handleDelete = async () => {
    if (!post || !selectedHolderId) return;
    setIsDeleting(true);
    try {
      await socialMediaApi.deleteSocialMediaPost(selectedHolderId, post.postId);
      navigate('/a/arche_social_media');
    } catch (err) {
      setIsDeleting(false);
      toast({
        title: 'Delete failed',
        description: err instanceof Error ? err.message : 'Failed to delete post',
        variant: 'destructive',
      });
    }
  };

  const isReadOnly = existingPost?.status === 'published' || existingPost?.status === 'publishing';

  const canDelete =
    existingPost?.status === 'draft' ||
    existingPost?.status === 'scheduled' ||
    existingPost?.status === 'cancelled' ||
    isReadOnly;

  const pageTitle = existingPost?.status
    ? PAGE_TITLES[existingPost.status]
    : postId
      ? 'Edit Post'
      : 'New Post';

  if (isLoadingPost) {
    return (
      <div className="p-6 space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  return (
    <div className="flex items-start min-h-full bg-social-bg">
      {/* Left Panel — Editor (~55%) */}
      <div className="flex-[55] min-w-0 p-6 lg:px-6 px-4 space-y-5 border-r border-border bg-social-bg pb-24 sm:pb-6">
        {/* Title + back navigation */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/a/arche_social_media/')}
                aria-label="Back"
                className="lg:px-3 !px-0"
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <h1 className="text-xl font-normal text-foreground font-satoshi">{pageTitle}</h1>
            </div>
            {canDelete && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleDelete}
                disabled={isDeleting}
                className="text-destructive hover:text-destructive hover:bg-destructive/10 text-[10px] font-mono"
                aria-label="Delete post"
              >
                <TrashIcon size={15} />
                <span className="">DELETE</span>
              </Button>
            )}
          </div>
        </div>

        <PostComposer
          post={post}
          content={content}
          setContent={setContent}
          title={title}
          setTitle={setTitle}
          notes={notes}
          setNotes={setNotes}
          targetAccountIds={targetAccountIds}
          setTargetAccountIds={setTargetAccountIds}
          tags={tags}
          onTagsChange={setTags}
          accounts={accounts}
          isSaving={isSaving}
          error={error}
          onSaveDraft={() => saveDraft(undefined, confirmedFileIds, link || null)}
          onAdapt={handleAdapt}
          onSchedule={handleSchedule}
          onPublishNow={handlePublishNow}
          onSavePlatformEdits={handleSavePlatformEdits}
          isAdapting={isAdapting}
          isReadOnly={isReadOnly}
          editedPlatformText={editedPlatformText}
          onEditedPlatformTextChange={setEditedPlatformText}
          onResetToDraft={handleResetToDraft}
          initialMediaFileIds={existingPost?.mediaFileIds ?? []}
          onMediaChange={setMediaAttachments}
          link={link}
          setLink={setLink}
          perPlatformSettings={perPlatformSettings}
          setPerPlatformSettings={setPerPlatformSettings}
          approvedPlatforms={approvedPlatforms}
          setApprovedPlatforms={setApprovedPlatforms}
        />
      </div>

      {/* Right Panel — Preview (~45%), desktop only */}
      <div className="hidden sm:block flex-[45] min-w-0 sticky top-0 max-h-screen overflow-y-auto bg-social-bg">
        <PostPreview
          content={content}
          accounts={accounts}
          targetAccountIds={targetAccountIds}
          mediaAttachments={mediaAttachments.filter((a) => a.uploadState === 'done')}
          platformTextOverrides={editedPlatformText}
          publishResults={post?.publishResults}
          link={link}
          perPlatformSettings={perPlatformSettings}
          approvedPlatforms={approvedPlatforms}
        />
      </div>

      {/* Mobile: floating preview button */}
      <button
        onClick={() => setIsPreviewOpen(true)}
        className="sm:hidden fixed bottom-6 right-5 z-40 flex items-center gap-2 rounded-full bg-primary text-primary-foreground shadow-lg px-4 py-3 text-sm font-medium active:scale-95 transition-transform"
        aria-label="Open preview"
      >
        <Eye className="h-4 w-4" />
        Preview
      </button>

      {/* Mobile: bottom sheet backdrop */}
      {isPreviewOpen && (
        <div
          className="sm:hidden fixed inset-0 z-40 bg-black/40"
          onClick={() => setIsPreviewOpen(false)}
        />
      )}

      {/* Mobile: bottom sheet */}
      <div
        ref={sheetRef}
        onTouchStart={handleSheetTouchStart}
        onTouchEnd={handleSheetTouchEnd}
        className={[
          'sm:hidden fixed bottom-0 left-0 right-0 z-50 flex flex-col',
          'bg-background rounded-t-2xl shadow-2xl',
          'transition-transform duration-300 ease-out',
          isPreviewOpen ? 'translate-y-0' : 'translate-y-full',
        ].join(' ')}
        style={{ height: '88vh' }}
        aria-modal="true"
        role="dialog"
        aria-label="Post preview"
      >
        {/* Drag handle */}
        <div className="flex justify-center pt-3 pb-1 flex-shrink-0">
          <div className="w-10 h-1 rounded-full bg-muted-foreground/30" />
        </div>

        {/* Sheet header */}
        <div className="flex items-center justify-between px-4 py-2 flex-shrink-0 border-b border-border">
          <span className="text-sm font-medium text-foreground">Preview</span>
          <button
            onClick={() => setIsPreviewOpen(false)}
            className="p-1 rounded-md text-muted-foreground hover:text-foreground"
            aria-label="Close preview"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Scrollable preview content */}
        <div className="flex-1 overflow-x-auto overflow-y-auto">
          <PostPreview
            content={content}
            accounts={accounts}
            targetAccountIds={targetAccountIds}
            mediaAttachments={mediaAttachments.filter((a) => a.uploadState === 'done')}
            platformTextOverrides={editedPlatformText}
            publishResults={post?.publishResults}
            link={link}
            perPlatformSettings={perPlatformSettings}
            approvedPlatforms={approvedPlatforms}
          />
        </div>
      </div>
    </div>
  );
}
